<template>
  <div class="parent-selectors">
    <v-sheet>
      <v-select
        v-for="filter in routeFilters"
        :key="filter.key"
        v-tooltip="filter.message"
        :label="filter.label"
        :color="filter.isDanger ? 'error' : 'primary'"
        :model-value="filter.value"
        :placeholder="filter.placeholder"
        :required="filter.isRequired"
        :items="
          filter.options.map((option) => ({
            value: option.id,
            title: option.name,
          }))
        "
        @update:model-value="filter.input"
      />
    </v-sheet>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, withDefaults } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { PRETTY_NAMES } from '@/models/core/defaults'
import { getAllAncestors, getDependencies } from '@/models/objectRegistry'

const LIST_FILTER = 'list-filter'
const OBJECT_CREATION = 'object-creation'
const route = useRoute()
const router = useRouter()
const store = useStore()

type ParentSelectorsProps = {
  objectType?: string
  type?: typeof LIST_FILTER | typeof OBJECT_CREATION
  showLabels?: boolean
}

const props = withDefaults(defineProps<ParentSelectorsProps>(), {
  objectType: '',
  type: LIST_FILTER,
  showLabels: true,
})

const routeFilters = computed(() => {
  const queryParameters =
    route.meta.hasQuery && Object.keys(route.meta.hasQuery)
  if (!queryParameters) {
    return []
  }

  const result = queryParameters.map((parameter) => {
    const value =
      store.getters['global/object'](parameter) &&
      store.getters[`global/object`](parameter).id
    const isRequired = filterIsRequired(parameter)
    const message =
      isRequired && !value
        ? `${PRETTY_NAMES[parameter]} is required.`
        : undefined

    let placeholder, deselectedText, isDanger
    if (props.type === LIST_FILTER) {
      placeholder = `Filter by ${PRETTY_NAMES[parameter]}`
      deselectedText = `- No filter -`
      isDanger = isRequired && !value
    } else {
      placeholder = `Select ${PRETTY_NAMES[parameter]}`
      deselectedText = `- Deselect -`
      isDanger = false
    }

    if (store.getters['global/collection'](parameter).objects.length === 0) {
      placeholder = `No ${PRETTY_NAMES[parameter]}s found`
    }

    let options = []

    if (store.getters['global/collection'](parameter).objects.length > 0) {
      if (!isRequired) {
        options.push({
          name: deselectedText,
        })
      }

      options = options.concat([
        ...store.getters['global/collection'](parameter).objects,
      ])
    }

    return {
      key: parameter,
      label: props.showLabels ? PRETTY_NAMES[parameter] : '',
      placeholder,
      value,
      input: filterInputFn(parameter),
      isRequired,
      isDanger,
      message,
      options,
    }
  })

  return result
})

onMounted(() => {
  if (props.type === OBJECT_CREATION && props.objectType === '') {
    throw new Error(
      `Prop objectType must be set when type is ${OBJECT_CREATION}`
    )
  }
})

function filterIsRequired(filteringBy: string) {
  if (props.objectType === '') {
    return false
  }
  const ancestors = getAllAncestors(props.objectType)
  return ancestors.some((ancestor) => {
    const requiredProp =
      props.type === LIST_FILTER ? 'requiredForFilter' : 'mainParent'
    return (
      ancestor.objectType === filteringBy && ancestor[requiredProp] === true
    )
  })
}

function filterInputFn(parameter: string) {
  return (id) => {
    const redirect = {
      path: route.path,
      query: {
        ...route.query,
        [parameter]: id,
      },
    }

    // clear selected page when changing a filter
    delete redirect.query.page

    for (const item of getDependencies(parameter)) {
      // delete depending object query parameters
      // e.g. when product changes, model must be cleared
      delete redirect.query[item]
    }

    router.push(redirect)
  }
}
</script>

<style lang="scss"></style>
